import React, { useState, useEffect, useCallback, useRef } from "react";
import { debounce } from "lodash";
import {
    Box,
    Button,
    CircularProgress,
    Grid,
    TextField,
    Typography,
    Card,
    CardContent,
    Tooltip,
    Autocomplete,
    Modal,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Divider,
} from "@mui/material";
import MainCard from "../../components/cards/MainCard";
import { getCardColor } from "../../utils/colorUtils";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import {
    useSaveUniversityCourseSettingsMutation,
    useGetUniversityCourseSettingsQuery,
} from "../../store/services/university-service";
import { useSelector } from "react-redux";
import { APP_URL } from "../../config";
import { useGetTenancyBasicsQuery, useGetTenancyByIdQuery } from "../../store/services/tenancy.service";
import { PRESET_TEMPLATES } from "../../themes/PresetTemplates";
import KeyboardArrowLeftTwoToneIcon from "@mui/icons-material/KeyboardArrowLeftTwoTone";
import { Editor } from "@tinymce/tinymce-react";
import { useLazyGetTinyJwtTokenQuery } from "../../store/services/simulation-service";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { isCustomReportedSimEmailConfigured } from "../../utils/custom-email-utils/customEmailValidation";

// Import the color transformation utilities
import {
    applyAllTransformationsForEditing,
    applyAllTransformationsForSaving
} from "../../utils/EmailTemplateTransformations";

function ReportedSimEmailEditor() {
    const navigate = useNavigate();
    const theme = useTheme();
    const lightMode = theme.palette.mode === "light";
    const { tenancyId } = useSelector((state) => state.tenancy);

    // State hooks
    const [isSaving, setIsSaving] = useState(false);
    const [placeholderModalOpen, setPlaceholderModalOpen] = useState(false);
    const [copiedStates, setCopiedStates] = useState({});
    const [editorContent, setEditorContent] = useState("");
    const [formData, setFormData] = useState({
        tenancyId: tenancyId,
        reportedSimBodyText: "",
        reportedSimDisplayName: "",
        reportedSimEmailSubject: "",
        useDefaultReportedSimTemplate: false,
    });

    // Query hooks - called with skip option when tenancyId is not available
    const { data: tenancyData, isLoading: tenancyLoading } = useGetTenancyByIdQuery(
        { tenancyId },
        { skip: !tenancyId }
    );

    const [globalEmailColors, setGlobalEmailColors] = useState({
        primary: tenancyData?.primaryHex || theme.palette.primary.main,
        secondary: tenancyData?.secondaryHex || theme.palette.secondary.main,
        tertiary: tenancyData?.tertiaryHex || theme.palette.warning.main,
    });

    const { data: tenancyBasics, isLoading: tenancyBasicsLoading } = useGetTenancyBasicsQuery(
        { tenancyId },
        { skip: !tenancyId }
    );

    const { data: settingsData, isLoading: settingsLoading } = useGetUniversityCourseSettingsQuery(
        { tenancyId },
        { skip: !tenancyId }
    );

    const { data: defaultTemplateSettings, isLoading: defaultTemplateLoading } =
        useGetUniversityCourseSettingsQuery(
            { tenancyId: 0 }
        );

    // Mutation hooks
    const [saveSettings] = useSaveUniversityCourseSettingsMutation();
    const [triggerJwtToken] = useLazyGetTinyJwtTokenQuery();

    // Callback hooks
    const copyToClipboard = useCallback(async (text, id) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedStates(prev => ({ ...prev, [id]: true }));
            setTimeout(() => {
                setCopiedStates(prev => ({ ...prev, [id]: false }));
            }, 1500);
        } catch (err) {
            console.error('Failed to copy: ', err);
        }
    }, []);

    const tinydriveTokenProvider = useCallback((callback) => {
        const getToken = async () => {
            try {
                const response = await triggerJwtToken().unwrap();
                if (response) {
                    callback(response);
                } else {
                    console.warn("JWT token not available in the response");
                    callback(null);
                }
            } catch (error) {
                console.error("Error fetching JWT token:", error);
                callback(null);
            }
        };

        getToken();
    }, [triggerJwtToken]);

    // Effect hooks
    useEffect(() => {
        if (defaultTemplateSettings || settingsData) {
            let templateToUse = defaultTemplateSettings;
            let usingDefault = true;

            if (settingsData) {
                const hasReportedSimConfig = isCustomReportedSimEmailConfigured(settingsData);
                usingDefault = settingsData.useDefaultReportedSimTemplate === true || !hasReportedSimConfig;
                templateToUse = usingDefault ? defaultTemplateSettings : settingsData;
            }

            if (templateToUse) {
                setFormData(prevFormData => ({
                    ...prevFormData,
                    ...(settingsData || {}),
                    tenancyId: tenancyId,
                    useDefaultReportedSimTemplate: usingDefault,
                    reportedSimBodyText: templateToUse.reportedSimBodyText || "",
                    reportedSimDisplayName: templateToUse.reportedSimDisplayName || "",
                    reportedSimEmailSubject: templateToUse.reportedSimEmailSubject || "",
                }));

                // Transform the template for editor display
                if (templateToUse.reportedSimBodyText) {
                    const logoUrl = tenancyData?.logo ? `${APP_URL}/${tenancyData.logo}` : null;
                    const transformedContent = applyAllTransformationsForEditing(
                        templateToUse.reportedSimBodyText,
                        globalEmailColors,
                        logoUrl
                    );
                    setEditorContent(transformedContent);
                }
            }
        }
    }, [settingsData, tenancyData, defaultTemplateSettings, tenancyId, globalEmailColors, settingsLoading, defaultTemplateLoading]);

    if (!tenancyId) {
        console.log('Early return: No tenancyId available');
        return (
            <Box sx={{ p: 2 }}>
                <Typography>Loading... (No tenancyId available)</Typography>
            </Box>
        );
    }

    // Updated placeholder list based on requirements
    const reportedSimPlaceholders = [
        { placeholder: "{simulationId}", description: "ID of the reported simulation" },
        { placeholder: "{originalSubject}", description: "Subject line of the reported phishing email" },
        { placeholder: "{fromAddress}", description: "From address of the reported phishing email" },
        { placeholder: "{reportedAt}", description: "Date and time when the email was reported" },
        { placeholder: "{firstName}", description: "First name of the person who reported" },
        { placeholder: "{lastName}", description: "Last name of the person who reported" },
        { placeholder: "{tenancyName}", description: "Name of the organisation" },
        { placeholder: "{client.logo}", description: "Organisation logo" },
        { placeholder: "{primaryColor}", description: "Primary brand colour" }
    ];

    const handleInputChange = (field) => (event) => {
        const newValue = event.target.value;
        setFormData((prev) => ({
            ...prev,
            [field]: newValue,
        }));
    };

    const handleEditorChange = (newContent) => {
        setEditorContent(newContent);

        // Update formData with the current editor content (don't transform yet)
        setFormData(prevFormData => ({
            ...prevFormData,
            reportedSimBodyText: newContent
        }));
    };

    const handleSave = async () => {
        if (!formData.tenancyId) {
            console.error("No tenancyId available");
            return;
        }

        setIsSaving(true);
        try {
            // Transform the editor content back to placeholders for saving
            const logoUrl = tenancyData?.logo ? `${APP_URL}/${tenancyData.logo}` : null;
            const transformedForSaving = applyAllTransformationsForSaving(
                editorContent,
                globalEmailColors,
                logoUrl
            );

            const settingsToSave = {
                ...formData,
                reportedSimBodyText: transformedForSaving,
                reportedSimDisplayName: formData.reportedSimDisplayName,
                reportedSimEmailSubject: formData.reportedSimEmailSubject,
                useDefaultReportedSimTemplate: false
            };

            await saveSettings({
                tenancyId: formData.tenancyId,
                settingsData: settingsToSave,
            }).unwrap();

            localStorage.setItem("reportedSimEmailColors", JSON.stringify(globalEmailColors));

            navigate(-1);
        } catch (error) {
            console.error("Failed to save reported sim email settings:", error);
        } finally {
            setIsSaving(false);
        }
    };

    const isLoading = settingsLoading || tenancyBasicsLoading || defaultTemplateLoading;

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    const editorStyle = `
        body { 
            background-color: ${lightMode ? 'white' : theme.palette.black.main}; 
            color: ${lightMode ? 'black' : theme.palette.grey[50]}; 
        }
    `;

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ flexGrow: 1, display: "flex" }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={12} lg={12}>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 2,
                            }}
                        >
                            <Typography variant="h2">Template Editor</Typography>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<KeyboardArrowLeftTwoToneIcon />}
                                    onClick={() => navigate(-1)}
                                    sx={{ whiteSpace: "nowrap" }}
                                >
                                    Back
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSave}
                                    disabled={isSaving}
                                    sx={{ whiteSpace: "nowrap" }}
                                >
                                    {isSaving ? 'Saving...' : 'Save & Apply'}
                                </Button>
                            </Box>
                        </Box>

                        {/* Email Configuration Card */}
                        <MainCard
                            content={true}
                            boxShadow={true}
                            border={false}
                            sx={{
                                bgcolor: getCardColor(lightMode),
                                mr: 2,
                                mb: 2
                            }}
                        >
                            <Typography variant="h6" sx={{ mb: 3 }}>
                                Email Configuration
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Display Name"
                                        value={formData.reportedSimDisplayName}
                                        onChange={handleInputChange('reportedSimDisplayName')}
                                        variant="outlined"
                                        helperText="The display name that will appear in the 'From' field of the email"
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Subject Line"
                                        value={formData.reportedSimEmailSubject}
                                        onChange={handleInputChange('reportedSimEmailSubject')}
                                        variant="outlined"
                                        helperText="The subject line for the reported simulation email"
                                    />
                                </Grid>
                            </Grid>
                        </MainCard>

                        {/* HTML Template Editor Card */}
                        <MainCard
                            content={true}
                            boxShadow={true}
                            border={false}
                            sx={{
                                bgcolor: getCardColor(lightMode),
                                mr: 2,
                                height: 1000
                            }}
                        >
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 2 }}>
                                <Typography variant="h6">
                                    HTML Template Editor
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    endIcon={<InfoIcon />}
                                    onClick={() => setPlaceholderModalOpen(true)}
                                >
                                    View Placeholders
                                </Button>
                            </Box>
                            <Editor
                                apiKey="j2g2hy77ifpdh2oe7hfarnk6vvls82qlv49guynyzw6d22ha"
                                value={editorContent}
                                onEditorChange={handleEditorChange}
                                init={{
                                    skin: lightMode ? 'oxide' : 'oxide-dark',
                                    content_css: lightMode ? 'default' : 'dark',
                                    content_style: editorStyle,
                                    plugins: [
                                        "advlist", "autolink", "lists", "link", "image", "charmap", "preview", "anchor",
                                        "searchreplace", "visualblocks", "code", "fullscreen",
                                        "insertdatetime", "media", "table", "code", "help", "wordcount", "template", "tinydrive"
                                    ],
                                    images_file_types: "jpg,png,jpeg,webp",
                                    tinydrive_token_provider: tinydriveTokenProvider,
                                    toolbar:
                                        "template image | code | undo redo | fontselect fontsize fontsizeselect bold italic forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link",
                                    templates: [
                                        ...reportedSimPlaceholders.map(p => ({
                                            title: p.placeholder,
                                            description: p.description,
                                            content: p.placeholder
                                        }))
                                    ],
                                    file_picker_types: 'media image',
                                    height: 900,
                                    min_height: 900,
                                    promotion: false,
                                }}
                            />
                        </MainCard>
                    </Grid>
                </Grid>
            </Box>

            {/* Placeholder Modal */}
            <Modal
                open={placeholderModalOpen}
                onClose={() => setPlaceholderModalOpen(false)}
                aria-labelledby="placeholder-modal-title"
                aria-describedby="placeholder-modal-description"
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "90%",
                        maxWidth: 600,
                        height: "80%",
                        boxShadow: theme.customShadows.z1,
                        backgroundColor: theme.palette.background.default,
                        borderRadius: theme.borderRadius.sm, // Border radius on outer container
                        outline: 'none',
                        overflow: 'hidden', // Hide any overflow to maintain border radius
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    {/* Fixed Header */}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            p: 3,
                            pb: 2,
                            flexShrink: 0 // Prevent header from shrinking
                        }}
                    >
                        <Typography id="placeholder-modal-title" variant="h4">
                            Available Placeholders
                        </Typography>
                        <IconButton
                            onClick={() => setPlaceholderModalOpen(false)}
                            size="small"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Divider sx={{ flexShrink: 0 }} />

                    {/* Scrollable Content Container */}
                    <Box
                        sx={{
                            flex: 1, // Take remaining space
                            overflowY: "auto", // Scrollable content
                            p: 3,
                            pt: 2
                        }}
                    >
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Click the copy icon to copy a placeholder to your clipboard, then paste it into the editor.
                        </Typography>
                        <List sx={{
                            bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                            borderRadius: theme.borderRadius.sm,
                            p: 1
                        }}>
                            {reportedSimPlaceholders.map((item, index) => (
                                <React.Fragment key={item.placeholder}>
                                    <ListItem
                                        sx={{
                                            py: 2,
                                            px: 2,
                                            '&:hover': {
                                                bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                                                borderRadius: 1
                                            }
                                        }}
                                    >
                                        <ListItemText
                                            primary={
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        fontFamily: 'monospace',
                                                        fontSize: '0.95rem',
                                                        fontWeight: 600,
                                                        color: theme.palette.primary.main
                                                    }}
                                                >
                                                    {item.placeholder}
                                                </Typography>
                                            }
                                            secondary={item.description}
                                            secondaryTypographyProps={{
                                                sx: { mt: 0.5, fontSize: '0.875rem' }
                                            }}
                                        />
                                        <ListItemSecondaryAction>
                                            <IconButton
                                                edge="end"
                                                onClick={() => copyToClipboard(item.placeholder, item.placeholder)}
                                                size="small"
                                                sx={{ ml: 2 }}
                                            >
                                                {copiedStates[item.placeholder] ?
                                                    <CheckIcon fontSize="small" color="success" /> :
                                                    <ContentCopyIcon fontSize="small" />
                                                }
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                    {index < reportedSimPlaceholders.length - 1 && <Divider variant="middle" />}
                                </React.Fragment>
                            ))}
                        </List>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
}

export default ReportedSimEmailEditor;